import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Images } from "lucide-react";
import { FadeUp, Eyebrow } from "./Reveal";
import Lightbox from "./Lightbox";
import { PROJECTS, PROJECT_CATEGORIES, EXECUTED, galleryOf } from "@/lib/projects";
import { EASE, scrollToId } from "@/lib/site";

const HEIGHTS = ["h-[500px]", "h-[400px]", "h-[440px]", "h-[400px]", "h-[500px]", "h-[440px]"];

function ProjectCard({ project, i, onOpen }) {
  const gallery = galleryOf(project);
  return (
    <motion.button
      layout
      data-testid={`project-card-${project.slug}`}
      onClick={() => onOpen({ images: gallery, index: 0 })}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease: EASE }}
      className="group text-left relative overflow-hidden bg-ink cursor-zoom-in"
    >
      <div className={`relative overflow-hidden ${HEIGHTS[i % HEIGHTS.length]} max-sm:h-[330px]`}>
        <img
          src={gallery[0].src}
          alt={gallery[0].alt}
          loading="lazy"
          className="w-full h-full object-cover img-editorial transition-transform duration-[1.3s] ease-out group-hover:scale-[1.06]"
        />
        <span className="absolute inset-0 bg-gradient-to-t from-ink/85 via-ink/10 to-transparent" />
        <span className="absolute top-5 left-5 flex items-center gap-1.5 bg-ink/70 backdrop-blur-sm text-cream/85 text-[10px] font-display tracking-widest2 uppercase px-3 py-2">
          <Images size={12} className="text-brass" /> {gallery.length}
        </span>
        <span className="absolute top-5 right-5 h-9 w-9 border border-cream/30 text-cream flex items-center justify-center opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
          <ArrowUpRight size={15} />
        </span>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-6 lg:p-7">
        <p className="font-display text-[10px] tracking-widest2 uppercase text-brass">
          {project.typology}
        </p>
        <h3 className="font-serif text-2xl lg:text-[1.75rem] text-cream mt-2 leading-tight">
          {project.name}
        </h3>
        <p className="text-[12.5px] text-cream/55 mt-1.5 tracking-wide">{project.location}</p>
        <p className="text-[13px] text-cream/0 group-hover:text-cream/65 leading-relaxed mt-0 group-hover:mt-3 max-h-0 group-hover:max-h-24 overflow-hidden transition-all duration-500">
          {project.blurb}
        </p>
      </div>
    </motion.button>
  );
}

export default function Projects() {
  const [cat, setCat] = useState("All");
  const [lb, setLb] = useState(null);
  const list = cat === "All" ? PROJECTS : PROJECTS.filter((p) => p.category === cat);

  return (
    <section id="projects" data-testid="projects-section" className="bg-cream py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <FadeUp>
          <Eyebrow>Selected Work · 19 Years</Eyebrow>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mt-5">
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-ink max-w-2xl leading-[1.05]">
              Residences, workplaces &amp; hospitality
            </h2>
            <p className="text-sm text-taupe max-w-sm leading-relaxed">
              From Lucknow villas and Delhi apartment renovations to a 36,000 sq.ft resort — every
              project delivered end-to-end, on budget and on time.
            </p>
          </div>
        </FadeUp>

        <FadeUp delay={0.08}>
          <div className="flex items-center gap-7 border-b border-hairline mt-12">
            {PROJECT_CATEGORIES.map((c) => (
              <button
                key={c}
                data-testid={`project-filter-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`relative pb-4 text-[13px] tracking-wide transition-colors duration-300 ${
                  cat === c ? "text-ink" : "text-taupe hover:text-ink"
                }`}
              >
                {c}
                <span className="ml-1.5 text-[10px] text-brass-deep align-top">
                  {c === "All" ? PROJECTS.length : PROJECTS.filter((p) => p.category === c).length}
                </span>
                {cat === c && (
                  <motion.span
                    layoutId="project-filter-underline"
                    className="absolute -bottom-px left-0 right-0 h-[2px] bg-brass"
                    transition={{ duration: 0.4, ease: EASE }}
                  />
                )}
              </button>
            ))}
          </div>
        </FadeUp>

        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
          <AnimatePresence mode="popLayout">
            {list.map((p, i) => (
              <ProjectCard key={p.slug} project={p} i={i} onOpen={setLb} />
            ))}
          </AnimatePresence>
        </motion.div>

        <FadeUp delay={0.06}>
          <button
            data-testid="projects-to-knights-court"
            onClick={() => scrollToId("knights-court")}
            className="group mt-5 w-full bg-sand border border-hairline p-7 lg:p-9 flex flex-col sm:flex-row sm:items-center justify-between gap-5 text-left hover:border-brass/50 transition-colors duration-300"
          >
            <div>
              <p className="font-display text-[10px] tracking-widest2 uppercase text-brass-deep">
                Signature case study
              </p>
              <p className="font-serif text-2xl lg:text-3xl text-ink mt-2">
                Jaypee Greens Knights Court, Noida
              </p>
              <p className="text-sm text-taupe mt-2">
                Room by room, raw site to handover — in four months, within ₹45 lakhs.
              </p>
            </div>
            <span className="shrink-0 flex items-center gap-2 text-[13px] tracking-wide text-ink border-b border-brass pb-1">
              Read the case study
              <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </button>
        </FadeUp>

        <FadeUp delay={0.06}>
          <div data-testid="executed-projects-list" className="mt-20 grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Eyebrow>Also executed</Eyebrow>
              <p className="font-serif text-3xl lg:text-4xl text-ink mt-4 leading-tight">
                A longer list, quietly delivered
              </p>
            </div>
            {Object.entries(EXECUTED).map(([group, items]) => (
              <div key={group} className="lg:col-span-4">
                <p className="font-display text-[10px] tracking-widest2 uppercase text-brass-deep border-b border-hairline pb-3">
                  {group}
                </p>
                <ul className="mt-4 space-y-2.5">
                  {items.map((it) => (
                    <li key={it} className="flex gap-3 text-[13.5px] text-espresso/80 leading-relaxed">
                      <span className="h-1 w-1 rotate-45 bg-brass shrink-0 mt-2" />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </FadeUp>
      </div>

      <Lightbox
        images={lb ? lb.images : []}
        index={lb ? lb.index : null}
        onClose={() => setLb(null)}
        onNavigate={(i) => setLb((cur) => (cur ? { ...cur, index: i } : cur))}
      />
    </section>
  );
}
